import Group from "../models/group.ts";
import MemberState from "../enums/member-state.ts";
import GroupStateEnum from "../enums/group-state.ts";
import StateUtils from "./state-utils.ts";
import Restaurant from "../models/restaurant.ts";

export default class GroupState {
  private _groups: Map<string, Group>;
  private _currentGroupId: string | null;

  constructor() {
    this._groups = new Map<string, Group>();
    this._currentGroupId = null;
  }

  /**
   * Gets the current Group that the user is interacting with.
   */
  public get currentGroupId(): string | null {
    return this._currentGroupId;
  }

  /**
   * Sets the ID for the Group the user is currently playing in.
   * @param groupId
   */
  public set currentGroupId(groupId: string | null) {
    this._currentGroupId = groupId;
  }

  /**
   * Adds a Group to the group state, if one hasn't already been added by that ID.
   *
   * Returns true if saved successfully.
   * Returns false if a group by that ID has already been added.
   */
  public addGroup(group: Group): boolean {
    // make sure no group by this ID has already been added
    if (this._groups.has(group.id)) {
      return false;
    }

    // save the group by ID
    this._groups.set(group.id, group);

    // success
    return true;
  }

  /**
   * Returns a Group by the given ID (if one has been added).
   */
  public getGroupById(groupId: string): Group | null {
    const group: Group | undefined = this._groups.get(groupId);
    return (group !== undefined) ? group : null;
  }

  /**
   * Returns a list of all Groups.
   */
  public get groups(): Group[] {
    const groups: Group[] = [];
    Array.from(this._groups.values()).forEach((group: Group) => groups.push(group));
    return groups;
  }

  /**
   * Updates the given Group's state.
   *
   * Returns false if a group with the given ID doesn't exist.
   * Returns false if the new GroupState matches the current one, if it goes backwards, or jumps too far ahead.
   * Returns true if the Group's state was updated successfully.
   */
  public updateGroupState(groupId: string, newGroupState: GroupStateEnum): boolean {
    // have to pull the group out of the Map<> and then re-assign it
    // (cannot mutate a Map's keys directly)
    const group: Group | undefined = this._groups.get(groupId);
    if (group === undefined) {
      // no group exists with the given ID
      return false;
    }

    // get original state
    const originalGroupState: GroupStateEnum = group.state;

    // make sure the GroupState isn't staying the same, going backwards, or jumping too far ahead
    if (!StateUtils.isNewGroupStateValid(originalGroupState, newGroupState)) {
      return false;
    }

    // update the Group's state
    group.state = newGroupState;

    // re-assign the group to the Map<> with the same ID
    this._groups.set(groupId, group);

    // success
    return true;
  }

  public updateGroupTopChoices(groupId: string, topRestaurants: Restaurant[]): boolean {
    // have to pull the group out of the Map<> and then re-assign it
    // (cannot mutate a Map's keys directly)
    const group: Group | undefined = this._groups.get(groupId);
    if (group === undefined) {
      // no group exists with the given ID
      return false;
    }

    // update the Group's top restaurants
    group.topRestaurants = topRestaurants;

    // re-assign the group to the Map<> with the same ID
    this._groups.set(groupId, group);

    // success
    return true;
  }

  public updateGroupBestRestaurant(groupId: string, bestRestaurant: string): boolean {
    // have to pull the group out of the Map<> and then re-assign it
    // (cannot mutate a Map's keys directly)
    const group: Group | undefined = this._groups.get(groupId);
    if (group === undefined) {
      // no group exists with the given ID
      return false;
    }

    // update the Group's top restaurants
    group.bestRestaurant = bestRestaurant;

    // re-assign the group to the Map<> with the same ID
    this._groups.set(groupId, group);

    // success
    return true;
  }

  /**
   * Adds the given Member to the Group by the given Group ID.
   *
   * If a Group doesn't exist with the given ID, returns false.
   * If the corresponding Group already has a Member with the given User ID, returns false.
   * If the Member is added successfully, returns true.
   */
  public addMemberToGroup(groupId: string, userId: string, memberState: MemberState): boolean {
    // have to pull the group out of the Map<> and then re-assign it
    // (cannot mutate a Map's keys directly)
    const group: Group | undefined = this._groups.get(groupId);
    if (group === undefined) {
      // no group exists with the given ID
      return false;
    }

    // make sure the group doesn't already have a member by the same ID
    if (group.members.has(userId)) {
      return false;
    }

    // add member to the group
    group.members.set(userId, memberState);

    // re-assign the group to the Map<> with the same ID
    this._groups.set(groupId, group);

    // success
    return true;
  }

  /**
   * Updates the given Group Member's state.
   *
   * Returns false if a group with the given ID doesn't exist.
   * Returns false if a group Member with the given User ID doesn't exist.
   * Returns false if the new MemberState matches the current one, if it goes backwards, or jumps too far ahead.
   * Returns true if the Group Member's state was updated successfully.
   */
  public updateMemberState(groupId: string, userId: string, newMemberState: MemberState): boolean {
    // have to pull the group out of the Map<> and then re-assign it
    // (cannot mutate a Map's keys directly)
    const group: Group | undefined = this._groups.get(groupId);
    if (group === undefined) {
      // no group exists with the given ID
      return false;
    }

    // get original state
    const originalMemberState: MemberState | undefined = group.members.get(userId);
    if (originalMemberState === undefined) {
      // group didn't contain a member by that ID
      return false;
    }

    // make sure the MemberState isn't staying the same, going backwards, or jumping too far ahead
    if (!StateUtils.isNewMemberStateValid(originalMemberState, newMemberState)) {
      return false;
    }

    // update the Member's state
    group.members.set(userId, newMemberState);

    // re-assign the group to the Map<> with the same ID
    this._groups.set(groupId, group);

    // success
    return true;
  }
}
